import React from 'react';
import {
    Box,
    Typography,
    Checkbox,
    FormControlLabel,
    FormGroup,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Chip,
    Stack,
    Divider,
    Alert,
} from '@mui/material';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { ExpandMore } from '@mui/icons-material';
/**
 * Export selection step component
 * Allows users to select which loops, designs, and content to include
 */
function ExportSelectionStep({ selections, onSelectionChange }) {
    const project = useSelector((state) => state.projects.selectedProject);
    const flatKpis = useSelector((state) => state.themes.flatKpis);

    if (!project || !project.phases || project.phases.length === 0) {
        return (
            <Alert severity="info">
                Dit project heeft nog geen ontwerpfases. Voeg eerst ontwerpfases toe om een export te kunnen maken.
            </Alert>
        );
    }

    // Get all loops from all phases
    const allLoops = project.phases.flatMap(phase =>
        (phase.loops || []).map(loop => ({
            ...loop,
            phaseName: phase.phase_name || `Fase ${phase.phase}`,
            phaseId: phase.id,
        }))
    );

    if (allLoops.length === 0) {
        return (
            <Alert severity="info">
                Dit project heeft nog geen ontwerprondes. Voeg eerst ontwerprondes toe om een export te kunnen maken.
            </Alert>
        );
    }

    // Handle loop selection toggle
    const handleLoopToggle = (loopId) => {
        const existingIndex = selections.loops.findIndex(l => l.loopId === loopId);
        
        if (existingIndex >= 0) {
            // Remove loop
            onSelectionChange({
                ...selections,
                loops: selections.loops.filter(l => l.loopId !== loopId),
            });
        } else {
            // Add loop with default selections
            const loop = allLoops.find(l => l.id === loopId);
            const designIds = (loop.designs || []).map(d => d.id);
            const selectedKpis = (loop.loop_kpis || [])
                .filter(lk => lk.is_selected)
                .map(lk => ({
                    kpiId: lk.kpi,
                    kpiName: flatKpis[lk.kpi]?.name || 'Unknown',
                }));
            
            // Add cost KPI
            selectedKpis.push({ kpiId: 'cost', kpiName: 'Kosten' });
            
            onSelectionChange({
                ...selections,
                loops: [
                    ...selections.loops,
                    {
                        loopId,
                        loopName: loop.name || `Loop ${loop.loop_number || ''}`,
                        includeSetup: true,
                        includeAdvice: true,
                        includeConclusions: true,
                        includeThemeGraph: loop.loop_themes && loop.loop_themes.length > 0,
                        includeScoresTable: true,
                        designs: designIds,
                        graphs: selectedKpis,
                    },
                ],
            });
        }
    };

    // Handle loop option change (setup, scores table)
    const handleLoopOptionChange = (loopId, option, value) => {
        onSelectionChange({
            ...selections,
            loops: selections.loops.map(l =>
                l.loopId === loopId ? { ...l, [option]: value } : l
            ),
        });
    };

    // Handle design selection
    const handleDesignToggle = (loopId, designId) => {
        onSelectionChange({
            ...selections,
            loops: selections.loops.map(l => {
                if (l.loopId !== loopId) return l;
                
                const designs = l.designs.includes(designId)
                    ? l.designs.filter(d => d !== designId)
                    : [...l.designs, designId];
                
                return { ...l, designs };
            }),
        });
    };

    // Handle graph selection
    const handleGraphToggle = (loopId, kpiId) => {
        onSelectionChange({
            ...selections,
            loops: selections.loops.map(l => {
                if (l.loopId !== loopId) return l;
                
                const graphs = l.graphs.some(g => g.kpiId === kpiId)
                    ? l.graphs.filter(g => g.kpiId !== kpiId)
                    : [...l.graphs, { 
                        kpiId, 
                        kpiName: kpiId === 'cost' ? 'Kosten' : flatKpis[kpiId]?.name || 'Unknown' 
                    }];
                
                return { ...l, graphs };
            }),
        });
    };

    // Select/deselect all
    const handleSelectAll = () => {
        const allLoopSelections = allLoops.map(loop => {
            const designIds = (loop.designs || []).map(d => d.id);
            const selectedKpis = (loop.loop_kpis || [])
                .filter(lk => lk.is_selected)
                .map(lk => ({
                    kpiId: lk.kpi,
                    kpiName: flatKpis[lk.kpi]?.name || 'Unknown',
                }));
            
            selectedKpis.push({ kpiId: 'cost', kpiName: 'Kosten' });
            
            return {
                loopId: loop.id,
                loopName: loop.name || `Loop ${loop.loop_number || ''}`,
                includeSetup: true,
                includeAdvice: true,
                includeConclusions: true,
                includeThemeGraph: true,
                includeScoresTable: true,
                designs: designIds,
                graphs: selectedKpis,
            };
        });
        
        onSelectionChange({ ...selections, loops: allLoopSelections });
    };

    const handleDeselectAll = () => {
        onSelectionChange({ ...selections, loops: [] });
    };

    const handleExplanationTextBoxesToggle = (event) => {
        onSelectionChange({
            ...selections,
            includeExplanationTextBoxes: event.target.checked,
        });
    };

    const isLoopSelected = (loopId) => selections.loops.some(l => l.loopId === loopId);
    const getLoopSelection = (loopId) => selections.loops.find(l => l.loopId === loopId);

    return (
        <Box>
            <Typography variant="h6" gutterBottom>
                Selecteer inhoud voor export
            </Typography>
            
            <Typography variant="body2" color="text.secondary" paragraph>
                Kies welke ontwerprondes, ontwerpen en grafieken je wilt opnemen in de Word export.
                De projectsamenvatting wordt altijd meegenomen.
            </Typography>

            <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
                <Chip 
                    label="Alles selecteren" 
                    onClick={handleSelectAll} 
                    color="primary" 
                    variant="outlined"
                    clickable
                />
                <Chip 
                    label="Alles deselecteren" 
                    onClick={handleDeselectAll} 
                    color="default" 
                    variant="outlined"
                    clickable
                />
            </Stack>

            <Divider sx={{ my: 2 }} />

            {/* Always included section */}
            <Box sx={{ p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
                <FormControlLabel
                    control={<Checkbox checked disabled />}
                    label={
                        <Box>
                            <Typography variant="subtitle1">Projectsamenvatting</Typography>
                            <Typography variant="body2" color="text.secondary">
                                Altijd inbegrepen: naam, aanvullende projectinformatie, team, startdatum, status
                            </Typography>
                        </Box>
                    }
                />
            </Box>
            {/* Optional bordered placeholders for handwritten notes in the Word export */}
            <Box sx={{ mb: 3, p: 2, pt: 1, borderRadius: 1 }}>
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={!!selections.includeExplanationTextBoxes}
                            onChange={handleExplanationTextBoxesToggle}
                        />
                    }
                    label={
                        <Box>
                            <Typography variant="subtitle1">Tekstvelden voor uitleg</Typography>
                            <Typography variant="body2" color="text.secondary">
                                Als u dit aanvinkt, worden lege tekstvelden opgenomen
                                onder de projectsamenvatting, onder de dijkvakken-tabel en onder elke
                                grafiek.
                            </Typography>
                        </Box>
                    }
                />
            </Box>

            {/* Loops section */}
            <Typography variant="subtitle1" gutterBottom sx={{ mt: 2 }}>
                Ontwerprondes ({selections.loops.length}/{allLoops.length} geselecteerd)
            </Typography>

            {allLoops.map((loop) => {
                const selected = isLoopSelected(loop.id);
                const loopSelection = getLoopSelection(loop.id);
                const loopName = loop.name || `Loop ${loop.loop_number || ''}`;
                
                return (
                    <Accordion 
                        key={loop.id} 
                        expanded={selected}
                        sx={{ mb: 1 }}
                    >
                        <AccordionSummary 
                            expandIcon={selected ? <ExpandMore color="text.white"/> : null}
                            sx={{ 
                                // '&:hover': { bgcolor: 'action.hover', color: 'text.black' },
                                bgcolor: selected ? 'primary.main' : 'transparent',
                            }}
                            onClick={() => handleLoopToggle(loop.id)}
                        >
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={selected}
                                        onChange={() => handleLoopToggle(loop.id)}
                                        onClick={(e) => e.stopPropagation()}
                                    />
                                }
                                label={
                                    <Box>
                                        <Typography variant="subtitle2">
                                            {loop.phaseName} - {loopName}
                                        </Typography>
                                        <Typography variant="caption">
                                            {loop.designs?.length || 0} ontwerp(en), {' '}
                                            {loop.loop_kpis?.filter(lk => lk.is_selected).length || 0} KPI's
                                        </Typography>
                                    </Box>
                                }
                                onClick={(e) => e.stopPropagation()}
                            />
                        </AccordionSummary>
                        
                        {selected && loopSelection && (
                            <AccordionDetails>
                                <Stack spacing={2}>
                                    {/* Loop configuration options */}
                                    <FormGroup>
                                        <FormControlLabel
                                            sx={{ml: 2}}
                                            control={
                                                <Checkbox
                                                    checked={loopSelection.includeSetup}
                                                    onChange={(e) => 
                                                        handleLoopOptionChange(loop.id, 'includeSetup', e.target.checked)
                                                    }
                                                />
                                            }
                                            label="Setup (thema's, KPI's, doelen, onderdelen, dijkvakken)"
                                        />
                                        <FormControlLabel
                                            sx={{ml: 4}}
                                            control={
                                                <Checkbox
                                                    checked={loopSelection.includeThemeGraph}
                                                    onChange={(e) => 
                                                        handleLoopOptionChange(loop.id, 'includeThemeGraph', e.target.checked)
                                                    }
                                                />
                                            }
                                            label="Grafiek met inspanningen per thema"
                                        />
                                        <FormControlLabel
                                            sx={{ml: 2}}
                                            control={
                                                <Checkbox
                                                    checked={loopSelection.includeAdvice}
                                                    onChange={(e) => 
                                                        handleLoopOptionChange(loop.id, 'includeAdvice', e.target.checked)
                                                    }
                                                />
                                            }
                                            label="Adviezen"
                                        />
                                        <FormControlLabel
                                        sx={{ml: 2}}
                                            control={
                                                <Checkbox
                                                    checked={loopSelection.includeScoresTable}
                                                    onChange={(e) => 
                                                        handleLoopOptionChange(loop.id, 'includeScoresTable', e.target.checked)
                                                    }
                                                />
                                            }
                                            label="Scores"
                                        />
                                        <FormControlLabel
                                            sx={{ml: 2}}
                                            control={
                                                <Checkbox
                                                    checked={loopSelection.includeConclusions}
                                                    onChange={(e) => 
                                                        handleLoopOptionChange(loop.id, 'includeConclusions', e.target.checked)
                                                    }
                                                />
                                            }
                                            label="Conclusies"
                                        />
                                    </FormGroup>

                                    <Divider />

                                    {/* Design selection */}
                                    {loop.designs && loop.designs.length > 0 && (
                                        <Box>
                                            <Typography variant="subtitle2" gutterBottom>
                                                Ontwerpen ({loopSelection.designs.length}/{loop.designs.length} geselecteerd)
                                            </Typography>
                                            <FormGroup>
                                                {loop.designs.map(design => (
                                                    <FormControlLabel
                                                        sx={{ml: 2}}
                                                        key={design.id}
                                                        control={
                                                            <Checkbox
                                                                checked={loopSelection.designs.includes(design.id)}
                                                                onChange={() => handleDesignToggle(loop.id, design.id)}
                                                            />
                                                        }
                                                        label={design.name}
                                                    />
                                                ))}
                                            </FormGroup>
                                        </Box>
                                    )}

                                    <Divider />

                                    {/* Graph/KPI selection */}
                                    <Box>
                                        <Typography variant="subtitle2" gutterBottom>
                                            Grafieken ({loopSelection.graphs.length}/{(loop.loop_kpis?.filter(lk => lk.is_selected).length || 0) + 1} geselecteerd)
                                        </Typography>
                                        <FormGroup>
                                            {/* Cost KPI */}
                                            <FormControlLabel
                                                sx={{ml: 2}}
                                                control={
                                                    <Checkbox
                                                        checked={loopSelection.graphs.some(g => g.kpiId === 'cost')}
                                                        onChange={() => handleGraphToggle(loop.id, 'cost')}
                                                    />
                                                }
                                                label="Kosten"
                                            />
                                            
                                            {/* Other KPIs */}
                                            {(loop.loop_kpis || [])
                                                .filter(lk => lk.is_selected)
                                                .map(loopKpi => {
                                                    const kpi = flatKpis[loopKpi.kpi];
                                                    if (!kpi) return null;
                                                    
                                                    return (
                                                        <FormControlLabel
                                                            sx={{ml: 2}}
                                                            key={loopKpi.id}
                                                            control={
                                                                <Checkbox
                                                                    checked={loopSelection.graphs.some(g => g.kpiId === kpi.id)}
                                                                    onChange={() => handleGraphToggle(loop.id, kpi.id)}
                                                                />
                                                            }
                                                            label={`${kpi.name}${kpi.unit ? ` (${kpi.unit})` : ''}`}
                                                        />
                                                    );
                                                })
                                            }
                                        </FormGroup>
                                    </Box>
                                </Stack>
                            </AccordionDetails>
                        )}
                    </Accordion>
                );
            })}

            {selections.loops.length === 0 && (
                <Alert severity="info" sx={{ mt: 2 }}>
                    Selecteer minimaal één ontwerpronde om te exporteren
                </Alert>
            )}
        </Box>
    );
}

ExportSelectionStep.propTypes = {
    selections: PropTypes.shape({
        projectInfo: PropTypes.bool,
        includeExplanationTextBoxes: PropTypes.bool,
        loops: PropTypes.arrayOf(PropTypes.shape({
            loopId: PropTypes.number,
            loopName: PropTypes.string,
            includeSetup: PropTypes.bool,
            includeThemeGraph: PropTypes.bool,
            includeScoresTable: PropTypes.bool,
            designs: PropTypes.arrayOf(PropTypes.number),
            graphs: PropTypes.arrayOf(PropTypes.shape({
                kpiId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
                kpiName: PropTypes.string,
            })),
        })),
    }).isRequired,
    onSelectionChange: PropTypes.func.isRequired,
};

export default ExportSelectionStep;

